/**
 * Inventory Helper Utilities
 * Functions for handling inventory freshness, timestamps, and display formatting
 */

/**
 * Get color code based on data freshness
 * @param {string|Date} timestamp - The timestamp to check
 * @returns {string} Material-UI color code
 */
export const getFreshnessColor = (timestamp) => {
  if (!timestamp) return 'text.disabled';

  const age = Date.now() - new Date(timestamp).getTime();
  const hours = age / (1000 * 60 * 60);

  if (hours < 24) return 'success.main';
  if (hours < 168) return 'warning.main'; // 7 days
  return 'error.main';
};

/**
 * Get freshness category from timestamp
 * @param {string|Date} timestamp - The timestamp to check
 * @returns {string} 'fresh' | 'recent' | 'stale' | 'unknown'
 */
export const getFreshnessCategory = (timestamp) => {
  if (!timestamp) return 'unknown';

  const age = Date.now() - new Date(timestamp).getTime();
  const hours = age / (1000 * 60 * 60);

  if (hours < 24) return 'fresh';
  if (hours < 168) return 'recent'; // 7 days
  return 'stale';
};

/**
 * Format timestamp as relative time (e.g., "2 hours ago", "3 days ago")
 * @param {string|Date} timestamp - The timestamp to format
 * @param {string} locale - Locale for formatting (default: 'zh-CN')
 * @returns {string} Formatted relative time string
 */
export const formatRelativeTime = (timestamp, locale = 'zh-CN') => {
  if (!timestamp) return '未知';

  try {
    const rtf = new Intl.RelativeTimeFormat(locale, { numeric: 'auto' });
    const diff = Date.now() - new Date(timestamp).getTime();
    const seconds = Math.floor(diff / 1000);
    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);
    const days = Math.floor(hours / 24);
    const weeks = Math.floor(days / 7);
    const months = Math.floor(days / 30);

    if (months > 0) return rtf.format(-months, 'month');
    if (weeks > 0) return rtf.format(-weeks, 'week');
    if (days > 0) return rtf.format(-days, 'day');
    if (hours > 0) return rtf.format(-hours, 'hour');
    if (minutes > 0) return rtf.format(-minutes, 'minute');
    return '刚刚';
  } catch (error) {
    console.error('Error formatting relative time:', error);
    return '未知';
  }
};

/**
 * Format timestamp as absolute time
 * @param {string|Date} timestamp - The timestamp to format
 * @param {boolean} includeTime - Whether to include time (default: true)
 * @returns {string} Formatted date string
 */
export const formatAbsoluteTime = (timestamp, includeTime = true) => {
  if (!timestamp) return '';

  try {
    const date = new Date(timestamp);
    const options = includeTime
      ? { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' }
      : { year: 'numeric', month: '2-digit', day: '2-digit' };

    return date.toLocaleString('zh-CN', options);
  } catch (error) {
    console.error('Error formatting absolute time:', error);
    return '';
  }
};

/**
 * Get warning message based on data freshness
 * @param {string} freshness - 'fresh' | 'recent' | 'stale' | 'unknown'
 * @returns {string|null} Warning message or null
 */
export const getDataFreshnessWarning = (freshness) => {
  switch(freshness) {
    case 'fresh':
      return null;
    case 'recent':
      return '数据可能不反映最新变化';
    case 'stale':
      return '数据已过时 - 使用前请验证';
    case 'unknown':
      return '数据新鲜度未知';
    default:
      return '数据状态未知';
  }
};

/**
 * Get stock level color based on quantity
 * @param {number} quantity - Current stock quantity
 * @param {number} threshold - Warning threshold (default: 50)
 * @returns {string} Material-UI color name
 */
export const getStockLevelColor = (quantity, threshold = 50) => {
  if (quantity === 0) return 'error';
  if (quantity < threshold) return 'warning';
  return 'success';
};

/**
 * Format quantity change with +/- indicator
 * @param {number} change - Quantity change
 * @param {string} unit - Unit of measurement (default: '支')
 * @returns {string} Formatted quantity change
 */
export const formatQuantityChange = (change, unit = '支') => {
  if (change === 0) return `0${unit}`;
  const sign = change > 0 ? '+' : '';
  return `${sign}${change}${unit}`;
};

/**
 * Select default location based on smart criteria
 * @param {Array} locations - Array of location objects with available, freshness, etc.
 * @returns {Object|null} Selected location or null
 */
export const selectDefaultLocation = (locations) => {
  if (!locations || locations.length === 0) return null;
  if (locations.length === 1) return locations[0];

  // Priority order:
  // 1. Location with fresh data and stock
  // 2. Location with most recent movement
  // 3. Location with highest stock

  const withStock = locations.filter(l => l.available_quantity > 0);
  if (withStock.length === 0) return locations[0];

  const fresh = withStock.filter(l => l.data_freshness === 'fresh');
  if (fresh.length > 0) {
    return fresh.sort((a, b) => b.available_quantity - a.available_quantity)[0];
  }

  // Sort by last movement date
  const withMovement = withStock.filter(l => l.last_movement?.created_at);
  if (withMovement.length > 0) {
    return withMovement.sort((a, b) =>
      new Date(b.last_movement.created_at) - new Date(a.last_movement.created_at)
    )[0];
  }

  // Fallback to highest stock
  return withStock.sort((a, b) => b.available_quantity - a.available_quantity)[0];
};

/**
 * Group inventory by product code
 * @param {Array} inventory - Flat inventory array with location details
 * @returns {Object} Grouped inventory by product code
 */
export const groupInventoryByProduct = (inventory) => {
  if (!inventory || !Array.isArray(inventory)) return {};

  return inventory.reduce((acc, item) => {
    const key = item.product_code || item.product_id;
    if (!key) return acc;

    if (!acc[key]) {
      acc[key] = {
        product_code: key,
        product_id: key,
        product_name: item.product_name || item.name,
        specification: item.specification,
        category: item.category,
        unit: item.unit,
        total_quantity: 0,
        total_frozen: 0,
        locations: []
      };
    }

    // Use NET available (remaining - reserved/frozen) so stock frozen for
    // sales orders cannot be allocated. available_quantity is always returned
    // by /api/inventory; fall back to remaining for safety.
    const remaining = item.remaining_quantity || 0;
    const qty = Math.max(0, item.available_quantity ?? remaining);
    const frozenQty = Math.max(0, remaining - qty);
    acc[key].locations.push({
      location: item.location,
      available_quantity: qty,
      frozen_quantity: frozenQty,
      // Track source client for cross-client stock transfers
      source_client_id: item.source_client_id || item.client_id,
      source_client_name: item.source_client_name || item.client_name,
      last_movement: item.last_movement,
      data_freshness: item.data_freshness,
      source_origins: item.source_origins || [],
      inbound_total: item.inbound_total,
      outbound_total: item.outbound_total,
      movement_count: item.movement_count
    });

    acc[key].total_quantity += qty;
    acc[key].total_frozen += frozenQty;

    return acc;
  }, {});
};

/**
 * Check if product requires location selection
 * @param {Object} product - Product object with locations array
 * @returns {boolean} True if manual selection needed
 */
export const requiresLocationSelection = (product) => {
  if (!product || !product.locations) return false;

  // Single location with stock - no selection needed
  const withStock = product.locations.filter(l => l.available_quantity > 0);
  return withStock.length > 1;
};

/**
 * Get movement type display info
 * @param {string} movementType - 'inbound' | 'outbound'
 * @returns {Object} Object with label, color, icon
 */
export const getMovementTypeInfo = (movementType) => {
  const types = {
    inbound: {
      label: '入库',
      color: 'success',
      icon: 'ArrowDownward'
    },
    outbound: {
      label: '出库',
      color: 'error',
      icon: 'ArrowUpward'
    },
    transfer: {
      label: '转库',
      color: 'info',
      icon: 'SwapHoriz'
    },
    adjustment: {
      label: '调整',
      color: 'warning',
      icon: 'Edit'
    }
  };

  return types[movementType] || {
    label: '未知',
    color: 'default',
    icon: 'Help'
  };
};

/**
 * Calculate total allocated quantity across locations
 * @param {Array} locations - Array of location objects with allocated_quantity
 * @returns {number} Total allocated quantity
 */
export const getTotalAllocated = (locations) => {
  if (!locations || !Array.isArray(locations)) return 0;
  return locations.reduce((sum, loc) => sum + (loc.allocated_quantity || 0), 0);
};

/**
 * Validate if allocation is possible
 * @param {Object} location - Location object
 * @param {number} requestedQty - Requested quantity
 * @returns {Object} Object with isValid and message
 */
export const validateAllocation = (location, requestedQty) => {
  if (!location) {
    return { isValid: false, message: '位置不存在' };
  }

  if (requestedQty <= 0) {
    return { isValid: false, message: '数量必须大于0' };
  }

  const available = location.available_quantity || 0;
  if (requestedQty > available) {
    return {
      isValid: false,
      message: `库存不足。可用: ${available}${location.unit || '支'}`
    };
  }

  // Check data freshness
  if (location.data_freshness === 'stale') {
    return {
      isValid: true,
      message: '警告: 数据已过时，建议先验证实际库存',
      warning: true
    };
  }

  return { isValid: true, message: '' };
};

/**
 * Get PO source display info
 * @param {Object} source - Source origin object
 * @returns {Object} Object with formatted display info
 */
export const formatPOSource = (source) => {
  if (!source) return null;

  return {
    reference: source.source_reference || 'PO-Unknown',
    supplier: source.supplier_name || '未知供应商',
    quantity: source.quantity || 0,
    date: source.received_date || source.movement_date,
    formattedDate: source.received_date
      ? formatAbsoluteTime(source.received_date, false)
      : '未知日期',
    sourceId: source.source_id
  };
};

/**
 * Sort locations by various criteria
 * @param {Array} locations - Array of location objects
 * @param {string} sortBy - 'freshness' | 'quantity' | 'location' | 'activity'
 * @returns {Array} Sorted locations array
 */
export const sortLocations = (locations, sortBy = 'freshness') => {
  if (!locations || !Array.isArray(locations)) return [];

  const sorted = [...locations];

  switch (sortBy) {
    case 'freshness':
      return sorted.sort((a, b) => {
        const freshnessOrder = { fresh: 0, recent: 1, stale: 2, unknown: 3 };
        const orderA = freshnessOrder[a.data_freshness] || 3;
        const orderB = freshnessOrder[b.data_freshness] || 3;

        if (orderA !== orderB) return orderA - orderB;
        // Secondary sort by quantity
        return b.available_quantity - a.available_quantity;
      });

    case 'quantity':
      return sorted.sort((a, b) => b.available_quantity - a.available_quantity);

    case 'location':
      return sorted.sort((a, b) =>
        (a.location || '').localeCompare(b.location || '')
      );

    case 'activity':
      return sorted.sort((a, b) => {
        const dateA = a.last_movement?.created_at
          ? new Date(a.last_movement.created_at).getTime()
          : 0;
        const dateB = b.last_movement?.created_at
          ? new Date(b.last_movement.created_at).getTime()
          : 0;
        return dateB - dateA;
      });

    default:
      return sorted;
  }
};

/**
 * Calculate freshness statistics for multiple locations
 * @param {Array} locations - Array of location objects
 * @returns {Object} Statistics object with counts
 */
export const getFreshnessStats = (locations) => {
  if (!locations || !Array.isArray(locations)) {
    return { fresh: 0, recent: 0, stale: 0, unknown: 0, total: 0 };
  }

  const stats = locations.reduce((acc, loc) => {
    const category = loc.data_freshness || 'unknown';
    acc[category] = (acc[category] || 0) + 1;
    acc.total += 1;
    return acc;
  }, { fresh: 0, recent: 0, stale: 0, unknown: 0, total: 0 });

  return stats;
};

const inventoryHelpers = {
  getFreshnessColor,
  getFreshnessCategory,
  formatRelativeTime,
  formatAbsoluteTime,
  getDataFreshnessWarning,
  getStockLevelColor,
  formatQuantityChange,
  selectDefaultLocation,
  groupInventoryByProduct,
  requiresLocationSelection,
  getMovementTypeInfo,
  getTotalAllocated,
  validateAllocation,
  formatPOSource,
  sortLocations,
  getFreshnessStats
};

export default inventoryHelpers;
